import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { LayoutDashboard, CheckSquare, LogOut, Users, Menu, X, Timer } from "lucide-react";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const ROLE_LABEL = {
  sales_executive:        "Sales Executive",
  sales_manager:          "Sales Manager",
  support_executive:      "Support Executive",
  verification_executive: "Verification Executive",
};

export default function StaffShell() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  useEffect(() => { api.get("/staff/me").then((r) => setMe(r.data)).catch(() => {}); }, []);

  const doLogout = async () => {
    try { await logout(); } catch { /* ignore */ }
    toast.success("Logged out");
    nav("/", { replace: true });
  };

  const navItems = [
    { to: "/staff",              label: "Dashboard",     icon: LayoutDashboard, end: true, testid: "staff-nav-dashboard" },
    { to: "/staff/verifications", label: "Verifications", icon: CheckSquare,                testid: "staff-nav-verifications" },
    { to: "/staff/trial-followups", label: "Trial Follow-ups", icon: Timer,                    testid: "staff-nav-trial-followups" },
  ];

  return (
    <div className="min-h-screen bg-slate-50" data-testid="staff-shell">
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200 h-14 flex items-center px-4">
        <button className="md:hidden mr-3 w-9 h-9 grid place-items-center rounded hover:bg-slate-100" onClick={() => setDrawerOpen(true)} data-testid="staff-mobile-menu-btn">
          <Menu className="w-5 h-5" />
        </button>
        <Logo size={28} />
        <div className="ml-2 font-display font-bold text-lg">HisaabPro</div>
        <div className="ml-2 hidden sm:inline text-xs font-semibold text-slate-500">Internal Staff Portal</div>
        <div className="ml-auto flex items-center gap-3">
          <div className="text-right hidden sm:block">
            <div className="text-sm font-semibold">{me?.full_name || user?.email}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">{ROLE_LABEL[me?.role || user?.role]}</div>
          </div>
          <button onClick={doLogout} className="text-slate-600 hover:text-red-600 p-2" title="Logout" data-testid="staff-logout"><LogOut className="w-4 h-4" /></button>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar (desktop) */}
        <aside className="hidden md:block w-56 border-r border-slate-200 bg-white sticky top-14 h-[calc(100vh-3.5rem)] px-3 py-4">
          {navItems.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-semibold mb-1 ${
                  isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50"
                }`}
              data-testid={it.testid}
            >
              <it.icon className="w-4 h-4" /> {it.label}
            </NavLink>
          ))}
        </aside>

        {/* Sidebar (mobile drawer) */}
        {drawerOpen && (
          <div className="fixed inset-0 z-40 md:hidden">
            <div className="absolute inset-0 bg-slate-900/50" onClick={() => setDrawerOpen(false)} />
            <div className="absolute left-0 top-0 h-full w-72 bg-white p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="font-bold">Menu</div>
                <button onClick={() => setDrawerOpen(false)} className="w-9 h-9 grid place-items-center rounded hover:bg-slate-100"><X className="w-5 h-5" /></button>
              </div>
              {navItems.map((it) => (
                <NavLink key={it.to} to={it.to} end={it.end} onClick={() => setDrawerOpen(false)} className={({ isActive }) => `flex items-center gap-2.5 rounded-md px-3 py-2 text-sm font-semibold mb-1 ${isActive ? "bg-emerald-50 text-emerald-700" : "text-slate-600 hover:bg-slate-50"}`}>
                  <it.icon className="w-4 h-4" /> {it.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}

        <main className="flex-1 p-4 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
